import apiClient from '@/api/index.js'
import { expandUPCE, isValidGS1Key } from '@/utils/gs1Validation'

const state = {
  products: [],
  product: null,
  categories: [],
  loading: false,
  error: null
}

const getters = {
  allProducts: state => state.products,
  productById: state => id => state.products.find(product => product.id === id),
  loading: state => state.loading,
  categories: state => state.categories
}

const actions = {
  fetchProducts({ commit }) {
    commit('SET_LOADING', true)
    apiClient.getProducts()
      .then(response => {
        commit('SET_PRODUCTS', response.data)
        commit('SET_LOADING', false)
      })
      .catch(error => {
        commit('SET_LOADING', false)
        console.error('Error fetching products:', error)
      })
  },
  async fetchCategories({ commit }) {
    try {
      const response = await apiClient.getCategories()
      commit('SET_CATEGORIES', response.data)
      return response.data
    } catch (error) {
      console.error('Error fetching categories:', error)
      throw error
    }
  },
  async createCategory({ commit }, category) {
    try {
      const response = await apiClient.createCategory(category)
      return response.data
    } catch (error) {
      console.error('Error creating category:', error)
      throw error
    }
  },
  fetchProduct({ commit }, id) {
    commit('SET_LOADING', true)
    apiClient.getProduct(id)
      .then(response => {
        commit('SET_PRODUCT', response.data)
        commit('SET_LOADING', false)
      })
      .catch(error => {
        commit('SET_LOADING', false)
        console.error('Error fetching product:', error)
      })
  },
  createProduct({ commit }, product) {
    commit('SET_LOADING', true)
    return apiClient.createProduct(product)
      .then(response => {
        const newProduct = response.data.product || response.data
        commit('ADD_PRODUCT', newProduct)
        commit('SET_LOADING', false)
        return newProduct
      })
      .catch(error => {
        commit('SET_LOADING', false)
        return Promise.reject(error)
      })
  },
  updateProduct({ commit }, product) {
    commit('SET_LOADING', true)
    return apiClient.updateProduct(product.id, product)
      .then(response => {
        const updatedProduct = response.data.product || response.data
        commit('UPDATE_PRODUCT', updatedProduct)
        commit('SET_LOADING', false)
        return updatedProduct
      })
      .catch(error => {
        commit('SET_LOADING', false)
        return Promise.reject(error)
      })
  },
  deleteProduct({ commit }, id) {
    commit('SET_LOADING', true)
    return apiClient.deleteProduct(id)
      .then(response => {
        commit('DELETE_PRODUCT', id)
        commit('SET_LOADING', false)
        return response.data
      })
      .catch(error => {
        commit('SET_LOADING', false)
        return Promise.reject(error)
      })
  },
  createBulkProducts({ commit }, products) {
    commit('SET_LOADING', true)
    return apiClient.createBulkProducts(products)
      .then(response => {
        // Los productos creados se cargarán con fetchProducts después
        commit('SET_LOADING', false)
        return response.data
      })
      .catch(error => {
        commit('SET_LOADING', false)
        return Promise.reject(error)
      })
  },
  findProductByBarcode({ state, commit }, barcode) {
    commit('SET_LOADING', true)
    return new Promise((resolve, reject) => {
      // Normaliza a GTIN-14 (rellena ceros a la izquierda) para que el EAN-13/UPC
      // guardado y el GTIN-14 que trae el código GS1 (AI 01) del mismo producto coincidan.
      // UPC-E (8 díg. no-EAN-8): su GTIN equivalente es el UPC-A expandido — la
      // expansión ocurre SOLO en esta comparación interna, para que el producto
      // matchee sin importar en cuál de las dos formas quedó guardado; un EAN-8
      // válido prevalece y no se expande.
      const toGtin14 = code => {
        let d = (code || '').toString().replace(/\D/g, '')
        if (d.length === 8 && !isValidGS1Key(d, [8])) {
          const upca = expandUPCE(d)
          if (upca) d = upca
        }
        return d.padStart(14, '0')
      }
      const target = toGtin14(barcode)
      // Matchea también el GTIN de la CAJA (unidad de despacho): al recepcionar
      // una caja (01) o un pallet por su (02), el producto se resuelve igual
      const product = state.products.find(p =>
        p.barcode === barcode ||
        toGtin14(p.barcode) === target ||
        (p.case_gtin && toGtin14(p.case_gtin) === target))
      if (product) {
        commit('SET_LOADING', false)
        resolve(product)
      } else {
        commit('SET_LOADING', false)
        reject(new Error('Producto no encontrado'))
      }
    })
  },
  findProductBySku({ commit }, sku) {
    commit('SET_LOADING', true)
    return apiClient.searchProductBySku(sku)
      .then(response => {
        commit('SET_LOADING', false)
        return response.data
      })
      .catch(error => {
        commit('SET_LOADING', false)
        return Promise.reject(error)
      })
  },
  // Método para búsqueda por SKU en backend (actualmente no usado, filtrado local es más eficiente)
  // searchProductsBySku({ commit }, query) {
  //   commit('SET_LOADING', true)
  //   return apiClient.searchProductsBySku(query)
  //     .then(response => {
  //       commit('SET_LOADING', false)
  //       return response.data
  //     })
  //     .catch(error => {
  //       console.error('🏪 Store searchProductsBySku error:', error)
  //       commit('SET_LOADING', false)
  //       return Promise.reject(error)
  //     })
  // }
}

const mutations = {
  SET_LOADING(state, loading) {
    state.loading = loading
  },
  SET_PRODUCTS(state, products) {
    state.products = products
  },
  SET_PRODUCT(state, product) {
    state.product = product
  },
  SET_CATEGORIES(state, categories) {
    state.categories = categories
  },
  ADD_CATEGORY(state, category) {
    state.categories.push(category)
  },
  ADD_PRODUCT(state, product) {
    state.products.push(product)
  },
  UPDATE_PRODUCT(state, updatedProduct) {
    const index = state.products.findIndex(p => p.id === updatedProduct.id)
    if (index !== -1) {
      state.products.splice(index, 1, updatedProduct)
    }
    if (state.product && state.product.id === updatedProduct.id) {
      state.product = updatedProduct
    }
  },
  DELETE_PRODUCT(state, id) {
    state.products = state.products.filter(p => p.id !== id)
    if (state.product && state.product.id === id) {
      state.product = null
    }
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
